import {
  AppShell,
  Container,
  Group,
  Image,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { PublicDisplaySettings } from "@tfg/types/endpoints/agec"

import classes from "~/assets/css/SearchEngine.module.css"
import { IconLogo } from "~/components/icons/logo/IconLogo"

type Props = {
  types: string[]
  brands: string[]
  searchEngineBrands: string[]
  collections: string[]
  values: URLSearchParams
  onFilter: (key: string, value: string | null) => void
  displaySetting: PublicDisplaySettings
}

export const SearchEngineHeader = ({
  brands,
  searchEngineBrands,
  types,
  collections,
  values,
  onFilter,
  displaySetting,
}: Props) => {
  const theme = useMantineTheme()
  const primaryColor =
    displaySetting?.outputTitlesColor ?? theme.colors.green[6]

  const { t } = useTranslation("public")
  return (
    <AppShell.Header py="sm" px="md" bg={displaySetting?.outputBackgroundColor}>
      <Container size="xl">
        <Stack h="100%" align="flex-start">
          <Group
            justify="space-between"
            w="100%"
            className={classes["title-mobile"]}
          >
            {displaySetting?.outputLogoUrl ? (
              <Image
                src={displaySetting.outputLogoUrl}
                className={classes["image-logo"]}
                w="auto"
                maw="90%"
              />
            ) : (
              <Title ff={displaySetting?.outputFontFamily ?? "inherit"}>
                {displaySetting?.slug}{" "}
                <Text component="span" size="sm">
                  {t("header.title")}
                </Text>
              </Title>
            )}
            <PoweredBy />
          </Group>

          <Group
            w="100%"
            justify="space-between"
            wrap="nowrap"
            className={classes["form-mobile"]}
          >
            <TextInput
              defaultValue={values.get("search") ?? ""}
              label={t("header.search.label")}
              placeholder={t("header.search.placeholder")}
              onChange={(e) => onFilter("search", e.target.value)}
              styles={{
                label: {
                  fontFamily: displaySetting?.outputFontFamily,
                  marginBottom: 2,
                },
                input: {
                  border: `1px solid ${displaySetting?.outputTitlesColor}`,
                  background: "white",
                },
              }}
            />
            <Group wrap="nowrap">
              {collections.length > 0 && (
                <Select
                  clearable
                  label={t("header.filters.label")}
                  disabled={collections.length === 0}
                  defaultValue={values.get("collection") ?? ""}
                  placeholder={t("header.filters.collection")}
                  data={collections}
                  onChange={(value) => onFilter("collection", value)}
                  styles={{
                    label: {
                      fontFamily: displaySetting?.outputFontFamily,
                      marginBottom: 2,
                    },
                    input: {
                      border: `1px solid ${primaryColor}`,
                      background: "white",
                    },
                  }}
                />
              )}

              <Select
                clearable
                mt="auto"
                data={types}
                label={
                  collections.length === 0
                    ? t("header.filters.label")
                    : undefined
                }
                disabled={types.length === 0}
                defaultValue={values.get("brandType") ?? ""}
                placeholder={t("header.filters.productType")}
                onChange={(value) => onFilter("brandType", value)}
                styles={{
                  input: {
                    border: `1px solid ${primaryColor}`,
                    background: "white",
                  },
                }}
              />
              {searchEngineBrands.length > 1 && (
                <Select
                  clearable
                  mt="auto"
                  data={brands}
                  defaultValue={values.get("brand") ?? ""}
                  placeholder={t("header.filters.brand")}
                  onChange={(value) => onFilter("brand", value)}
                  styles={{
                    input: {
                      border: `1px solid ${primaryColor}`,
                      background: "white",
                    },
                  }}
                />
              )}
            </Group>
          </Group>
        </Stack>
      </Container>
    </AppShell.Header>
  )
}

export const PoweredBy = () => {
  window.innerWidth

  const [height, setHeight] = useState<number>(24)

  useEffect(() => {
    if (window.innerWidth < 500) {
      setHeight(12)
    }
  }, [setHeight])

  return (
    <Group justify="center" className={classes["subtitle-mobile"]}>
      <Text
        fz="sm"
        style={{
          fontFamily: "sans-serif",
          display: "flex",
          alignItems: "center",
        }}
      >
        Powered by
      </Text>
      <IconLogo height={height} />
    </Group>
  )
}
